import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './AppShell.module.css';

type AppShellProps = {
  children: React.ReactNode;
  variant?: 'marketing' | 'console';
  heroWatchId?: string;
};

type NavItem = {
  label: string;
  to: string;
  hash?: string;
  external?: boolean;
};

const marketingNav: NavItem[] = [
  { label: 'Platform', to: '/' },
  { label: 'Solutions', to: '/solutions' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Resources', to: '/resources' },
  { label: 'Marketplace', to: '/marketplace' },
  { label: 'Studio', to: '/studio' },
];

const footerSections: Array<{ title: string; links: NavItem[] }> = [
  {
    title: 'Engineering',
    links: [
      { label: 'Docs', to: '/docs', external: true },
      { label: 'CLI reference', to: '/docs/cli', external: true },
      { label: 'Changelog', to: '/docs/changelog', external: true },
    ],
  },
  {
    title: 'Operations',
    links: [
      { label: 'Compliance', to: '/resources/compliance', external: true },
      { label: 'Runbooks', to: '/resources/runbooks', external: true },
      { label: 'Status', to: 'https://status.deployrelay.app', external: true },
    ],
  },
  {
    title: 'Revenue',
    links: [
      { label: 'Pricing guide', to: '/pricing' },
      { label: 'ROI brief', to: '/resources/roi', external: true },
      { label: 'Customer stories', to: '/resources/customers', external: true },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', to: '/about', external: true },
      { label: 'Careers', to: '/careers', external: true },
      { label: 'Press', to: '/press', external: true },
    ],
  },
];

const AppShell: React.FC<AppShellProps> = ({ children, variant = 'marketing', heroWatchId }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const isMarketing = variant === 'marketing';
  const [ctaVisible, setCtaVisible] = useState(!isMarketing || !heroWatchId);

  useEffect(() => {
    if (!isMarketing || !heroWatchId) {
      setCtaVisible(true);
      return;
    }

    const target = document.getElementById(heroWatchId);
    if (!target) {
      setCtaVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setCtaVisible(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0.2,
      },
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [isMarketing, heroWatchId]);

  const renderNavLinks = () => (
    <nav
      className={styles.navLinks}
      aria-label={isMarketing ? 'Primary navigation' : 'Console navigation'}
    >
      {marketingNav.map((item) =>
        item.external ? (
          <a
            key={item.label}
            href={item.to}
            className={styles.navLink}
            target="_blank"
            rel="noreferrer"
          >
            {item.label}
          </a>
        ) : (
          <Link
            key={item.label}
            to={item.to}
            className={styles.navLink}
            onClick={() => setMenuOpen(false)}
          >
            {item.label}
          </Link>
        ),
      )}
    </nav>
  );

  return (
    <div className={styles.shell}>
      {isMarketing && (
        <div className={styles.announcement}>
          <span>New:</span>
          <span>Relay Studio beta is open for early access.</span>
          <a href="/studio" className={styles.announcementLink}>
            Explore Studio →
          </a>
        </div>
      )}

      {isMarketing && (
        <header className={styles.navbar}>
          <div className={styles.navInner}>
            <Link to="/" className={styles.brand} onClick={() => setMenuOpen(false)}>
              <span className={styles.brandBadge}>R</span>
              <span>Relay</span>
            </Link>

            {renderNavLinks()}

            <div
              className={`${styles.ctaGroup} ${ctaVisible ? styles.ctaVisible : styles.ctaHidden}`}
              aria-hidden={!ctaVisible}
            >
              <Link to="/docs" className={styles.secondaryCta}>
                Explore docs
              </Link>
              <Link to="/dashboard" className={styles.primaryCta}>
                Launch console
              </Link>
            </div>

            <button
              type="button"
              className={styles.menuToggle}
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-expanded={menuOpen}
              aria-label="Toggle navigation"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M4 6h16M4 12h16M4 18h16"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {menuOpen && (
            <div className={styles.drawer}>
              <div className={styles.drawerList}>
                {marketingNav.map((item) =>
                  item.external ? (
                    <a
                      key={item.label}
                      href={item.to}
                      className={styles.drawerLink}
                      onClick={() => setMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      key={item.label}
                      to={item.to}
                      className={styles.drawerLink}
                      onClick={() => setMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ),
                )}
              </div>
              <div className={styles.drawerCta}>
                <Link to="/docs" className={styles.secondaryCta} onClick={() => setMenuOpen(false)}>
                  Explore docs
                </Link>
                <Link to="/dashboard" className={styles.primaryCta} onClick={() => setMenuOpen(false)}>
                  Launch console
                </Link>
              </div>
            </div>
          )}
        </header>
      )}

      <main className={styles.main}>{children}</main>

      {isMarketing && (
        <footer id="company" className={styles.footer}>
          <div className={styles.footerInner}>
            <div className={styles.footerBrand}>
              <div>
                <span className={styles.brandBadge}>R</span>
              </div>
              <p>
                Relay is the control plane for shared serverless logic—govern identity, execution,
                and monetization across every cloud from a single platform.
              </p>
              <p>© {new Date().getFullYear()} Relay Protocol. All rights reserved.</p>
            </div>
            {footerSections.map((section) => (
              <div key={section.title}>
                <h3 className={styles.footerTitle}>{section.title}</h3>
                {section.links.map((link) =>
                  link.external ? (
                    <a
                      key={link.label}
                      href={link.to}
                      className={styles.footerLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link key={link.label} to={link.to} className={styles.footerLink}>
                      {link.label}
                    </Link>
                  ),
                )}
              </div>
            ))}
          </div>
          <div className={styles.footerBottom}>
            <span>Built on Neon, Upstash, Railway, and Datadog.</span>
            <span>Trust center · Privacy · Terms</span>
          </div>
        </footer>
      )}
    </div>
  );
};

export default AppShell;
